import React, { useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Typography, Divider, theme } from 'antd';
import { SensorsKPICardContainer } from './SensorsKPITab.styles';
import { EPSelectOption } from '@/components';
import { useStores } from '@/hooks/use-stores';
import { EngineConfigStatus, SensorsLogicEnum } from '@/store/Engine/constants';
import { t } from 'i18next';
import { observer } from 'mobx-react';
import find from 'lodash/find';
import {
  handleAddFallback,
  handleDeleteIconClick,
  handleSensorPriorityFieldChange,
  KPISensorCardProps,
  validateOnSubmit,
  PRESSURE_TYPE,
  FLOW_TYPE,
} from './SensorsKPITab.utils';
import KPISensorCardTitle from './KPISensorCardTitle';
import KPISensorRow from './KPISensorRow';

const { Title } = Typography;
const { useToken } = theme;

const KPISensorCard: React.FC<KPISensorCardProps> = observer(
  ({
    title,
    sensorsPriority,
    icon,
    type,
    sensorsOptions,
    setIsSaveDisabled,
    hasTriedToSubmit,
    isPressureFallbackAllowed,
    isFlowFallbackAllowed,
    isAlwaysReadOnly,
  }) => {
    const { engineStore, devicesStore } = useStores();
    const { token } = useToken();

    const mainPriority = useMemo(() => sensorsPriority?.sensorsPriority?.[0], [sensorsPriority?.sensorsPriority]);

    const onDeleteIconClick = useCallback(handleDeleteIconClick(sensorsPriority, setIsSaveDisabled), [
      sensorsPriority,
      setIsSaveDisabled,
    ]);

    const addFallback = useCallback(handleAddFallback(sensorsPriority, type, engineStore, setIsSaveDisabled), [
      sensorsPriority,
      type,
      engineStore?.sensorsConfiguration,
      setIsSaveDisabled,
    ]);

    const onSensorPriorityFieldChange = useCallback(
      handleSensorPriorityFieldChange(sensorsPriority, engineStore, devicesStore, type, setIsSaveDisabled),
      [sensorsPriority, engineStore, devicesStore, type, setIsSaveDisabled],
    );

    const handleStatus = useCallback(
      key => {
        const message = find(engineStore.validationMessages, {
          key: key,
          field: 'sensors',
          focused: true,
        })?.message;

        return message ? 'error' : undefined;
      },
      [engineStore.validationMessages],
    );

    useEffect(() => {
      if (setIsSaveDisabled && sensorsPriority?.sensorsPriority) {
        const hasPriorityWithNoSensors = sensorsPriority.sensorsPriority.some(
          priority => !priority.sensors || priority.sensors.length === 0,
        );

        if (hasPriorityWithNoSensors) {
          setIsSaveDisabled(true);
        }
      }
    }, [sensorsPriority?.sensorsPriority, setIsSaveDisabled]);

    useEffect(() => {
      validateOnSubmit(hasTriedToSubmit, engineStore, type);
    }, [hasTriedToSubmit, engineStore, type]);

    const logicOptions = useMemo(
      () =>
        Object.values(SensorsLogicEnum)
          .map(aggregation =>
            aggregation === SensorsLogicEnum.Sum && type === PRESSURE_TYPE ? null : (
              <EPSelectOption value={aggregation} key={aggregation}>
                {t(aggregation)}
              </EPSelectOption>
            ),
          )
          .filter(Boolean),
      [type],
    );

    const enableMainPriority = useMemo(() => {
      return (
        !engineStore.kpiSyncEnabled &&
        engineStore.configurationStatus !== EngineConfigStatus.Published &&
        engineStore.configurationStatus !== EngineConfigStatus.Recommendation
      );
    }, [engineStore.kpiSyncEnabled, engineStore.configurationStatus]);

    const showAddFallback = useMemo(() => {
      return (type === PRESSURE_TYPE && isPressureFallbackAllowed) || (type === FLOW_TYPE && isFlowFallbackAllowed);
    }, [type, isPressureFallbackAllowed, isFlowFallbackAllowed]);

    const fallbackPriorities = useMemo(
      () =>
        sensorsPriority?.sensorsPriority?.slice(1).map(priority => (
          <div key={priority.key || priority.id}>
            <Divider className="divider" />
            <strong className="section-title">{priority?.getPriorityName(t)}</strong>
            <KPISensorRow
              priority={priority}
              showDeleteButton={true}
              logicOptions={logicOptions}
              sensorsOptions={sensorsOptions}
              handleStatus={handleStatus}
              onSensorPriorityFieldChange={onSensorPriorityFieldChange}
              onDeleteIconClick={onDeleteIconClick}
              devicesStore={devicesStore}
              engineStore={engineStore}
              type={type}
              token={token}
              isAlwaysReadOnly={isAlwaysReadOnly}
            />
          </div>
        )),
      [
        sensorsPriority?.sensorsPriority,
        logicOptions,
        sensorsOptions,
        devicesStore,
        engineStore,
        type,
        token,
        onDeleteIconClick,
      ],
    );

    const fallbackCount =
      sensorsPriority?.sensorsPriority?.length > 1 ? sensorsPriority.sensorsPriority.length - 1 : null;

    return (
      <SensorsKPICardContainer>
        <Card
          title={<KPISensorCardTitle icon={icon} title={title} fallbackCount={fallbackCount} />}
          extra={
            showAddFallback && (
              <Button type="default" onClick={addFallback} disabled={engineStore.isReadOnly}>
                {t('addFallback')}
              </Button>
            )
          }
        >
          <Title level={5} className="sensor-section-title">
            {mainPriority?.getPriorityName(t)}
          </Title>

          {mainPriority && (
            <KPISensorRow
              priority={mainPriority}
              showDeleteButton={false}
              logicOptions={logicOptions}
              sensorsOptions={sensorsOptions}
              handleStatus={handleStatus}
              onSensorPriorityFieldChange={onSensorPriorityFieldChange}
              onDeleteIconClick={onDeleteIconClick}
              devicesStore={devicesStore}
              engineStore={engineStore}
              type={type}
              token={token}
              isAlwaysReadOnly={!enableMainPriority}
            />
          )}

          {fallbackPriorities}
        </Card>
      </SensorsKPICardContainer>
    );
  },
);

export default React.memo(KPISensorCard);
